/**
 * Album topicXData helpers — FE-owned JSON on the ALBUM topic.
 * Same merge discipline as galleryXData: never wipe unknown keys.
 */

export interface AlbumXData {
  /**
   * When false, hide lightbox “Download original” (and soft-block image
   * context menu). Missing → true (legacy always-on behavior).
   */
  allowDownload?: boolean
  /**
   * The 3D gallery topic created from this album via the wizard. Written
   * back onto the ALBUM's own xdata (mirroring how the gallery stores
   * `sourceAlbumId` on its own xdata) so both the owner's single-album view
   * and the unauthenticated guest view can resolve it — neither can run
   * useAlbumLibrary's authenticated cross-reference of "my galleries".
   */
  galleryId?: string
  /**
   * The `egoID` of the photo the owner picked as this album's cover, shown
   * in library grids/hero instead of the always-shifting "latest upload"
   * default. Missing → falls back to latestEgo/first photo (topicCoverUrl).
   */
  coverEgoId?: string
  /**
   * Owner-chosen display order, as `egoID`s. There's no backend "reorder
   * this album" primitive — a photo's natural position comes from which
   * upload-batch note it landed in plus its slot in that note's own
   * `egoList` (see `mappers.ts`' `notePhotosToAlbumPhotos`), and there's no
   * single field spanning every note to rewrite instead. This is the same
   * FE-owned-JSON workaround as `coverEgoId`/`allowDownload`. Only egoIds
   * present here are reordered; anything else (new uploads since the last
   * reorder, or the rare photo missing an `egoID` entirely) keeps sorting
   * to the end in its natural order — see `sortPhotosByOrder`.
   */
  photoOrder?: string[]
  /**
   * Owner opted into higher-quality uploads for this album. Only honored if
   * the owner's live tier still qualifies at upload time (re-checked via
   * `isFeatureAvailable(FEATURE_KEYS.FULL_RESOLUTION_UPLOAD)`, never trusted
   * from storage alone) — storing it doesn't grant the capability, it's just
   * the owner's stated intent. Settable both at album creation and later
   * from the Share sheet; same field backs both surfaces. Missing → false
   * (today's 1000px behavior).
   */
  fullResolutionUploads?: boolean
  [key: string]: unknown
}

function parseRaw(raw?: string | null): AlbumXData {
  if (!raw || raw === 'X Data Here') return {}
  try {
    const parsed = JSON.parse(raw) as unknown
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {}
    return parsed as AlbumXData
  } catch {
    return {}
  }
}

/** Default true when unset / placeholder / malformed. */
export function parseAllowDownload(topicXData?: string | null): boolean {
  const data = parseRaw(topicXData)
  if (typeof data.allowDownload === 'boolean') return data.allowDownload
  return true
}

/** Merge allowDownload into existing xdata without dropping other keys. */
export function withAllowDownload(topicXData: string | undefined | null, allowDownload: boolean): string {
  const data = parseRaw(topicXData)
  const next: AlbumXData = { ...data, allowDownload }
  return JSON.stringify(next)
}

/** The linked 3D gallery's topic id, if this album has been converted. */
export function parseGalleryId(topicXData?: string | null): string | undefined {
  const data = parseRaw(topicXData)
  return typeof data.galleryId === 'string' && data.galleryId ? data.galleryId : undefined
}

/** Merge galleryId into existing xdata without dropping other keys. */
export function withGalleryId(topicXData: string | undefined | null, galleryId: string): string {
  const data = parseRaw(topicXData)
  const next: AlbumXData = { ...data, galleryId }
  return JSON.stringify(next)
}

/** The owner-picked cover photo's egoID, if one was ever set. */
export function parseCoverEgoId(topicXData?: string | null): string | undefined {
  const data = parseRaw(topicXData)
  return typeof data.coverEgoId === 'string' && data.coverEgoId ? data.coverEgoId : undefined
}

/** Merge coverEgoId into existing xdata without dropping other keys. */
export function withCoverEgoId(topicXData: string | undefined | null, coverEgoId: string): string {
  const data = parseRaw(topicXData)
  const next: AlbumXData = { ...data, coverEgoId }
  return JSON.stringify(next)
}

/** The owner-chosen photo display order (egoIDs), if one was ever set. */
export function parsePhotoOrder(topicXData?: string | null): string[] | undefined {
  const data = parseRaw(topicXData)
  return Array.isArray(data.photoOrder) && data.photoOrder.every((v) => typeof v === 'string')
    ? (data.photoOrder as string[])
    : undefined
}

/** Merge photoOrder into existing xdata without dropping other keys. */
export function withPhotoOrder(topicXData: string | undefined | null, photoOrder: string[]): string {
  const data = parseRaw(topicXData)
  const next: AlbumXData = { ...data, photoOrder }
  return JSON.stringify(next)
}

/** Default false when unset / placeholder / malformed. */
export function parseFullResolutionUploads(topicXData?: string | null): boolean {
  const data = parseRaw(topicXData)
  return data.fullResolutionUploads === true
}

/** Merge fullResolutionUploads into existing xdata without dropping other keys. */
export function withFullResolutionUploads(
  topicXData: string | undefined | null,
  fullResolutionUploads: boolean,
): string {
  const data = parseRaw(topicXData)
  const next: AlbumXData = { ...data, fullResolutionUploads }
  return JSON.stringify(next)
}

/** Clean JSON for new albums (replaces placeholder "X Data Here"). */
export function emptyAlbumXData(fullResolutionUploads = false, allowDownload = true): string {
  return JSON.stringify({ allowDownload, fullResolutionUploads })
}
